import * as Context from 'contexts/JWTContext';
import * as authentication from 'models/authentication';
import * as jwt from 'models/jwt';
import {
  useCallback, useEffect, useRef,
} from 'react';

type ReturnType = [jwt.identity.IJWTIdentity | undefined, authentication.LoginResult | undefined];
/**
 * A hook that enables JWT authentication and keeps the user signed in.
 * @returns The user that is currently signed in.
 */
export const useJWTAuthentication = (): ReturnType => {
  const timeout = useRef<NodeJS.Timeout>();
  const [tokens, setTokens] = Context.useJWTokens();

  const user = Context.useJWTUser();

  const reauthenticate = useCallback(async (signal?: AbortSignal) => {
    if (!tokens) return;
    const jUser = jwt.identity.Parse(tokens.token);
    if (jwt.identity.WasJustIssued(jUser)) return;
    try {
      const response = await authentication.api.Refresh(tokens, signal);
      setTokens(response.data);
      console.info('Reauthenticated!');
    } catch (error) {
      console.error('Failed to authenticate', error);
      setTokens(undefined);
    }
  }, [tokens, setTokens]);

  useEffect(() => {
    const controller = new AbortController();
    if (user) {
      const expiry = jwt.identity.GetTimeUntilExpiry(user);
      // Reauthenticate before the token expires
      const jitterPercent = (Math.random() + 3) / 100;
      const secondsBefore = expiry * jitterPercent;
      const timeUntilExpire = Math.max(0, expiry - secondsBefore);
      console.log(`Reauthenticating in ${timeUntilExpire} seconds`);
      timeout.current = setTimeout(() => reauthenticate(controller.signal), timeUntilExpire * 1000);
    }
    return () => {
      if (timeout.current) clearTimeout(timeout.current);
      controller.abort();
    };
  }, [reauthenticate, user]);

  return [user, tokens];
};
export default useJWTAuthentication;
